/**
 * Author: Stefan Haack (https://shaack.com)
 */

import {GambitWebSocketConnection, gameEventAction} from "./websocket/GambitWebSocketConnection.js"
import {GambitLocalPlayer} from "../chess-console/players/GambitLocalPlayer.js"
import {COLOR} from "../../lib/cm-chessboard/Chessboard.js"
import {GambitChessConsole} from "../chess-console/GambitChessConsole.js"
import {GambitRemotePlayer} from "../chess-console/players/GambitRemotePlayer.js"
import {Observed} from "../../lib/cm-web-modules/observed/Observed.js"
import {ChessConsoleAppState, PLAYER_STATE} from "./ChessConsoleAppState.js"
import {EndOfGameActions} from "./components/EndOfGameActions.js"
import {get} from "svelte/store"
import {sessionData} from "../stores.js"
import {debugLog} from "../lib.js"



export class ChessConsoleApp {
    constructor(context, props) {
        this.props = {
            debug: true,
            resourcesCacheKey: "2303131053"
        }
        Object.assign(this.props, props)
        debugLog(this.props.debug, "ChessConsoleApp props", props)
        this.context = context
        this.webSocketConnection = new GambitWebSocketConnection(
            {
                onInitGame: this.onInitGame.bind(this),
                onGameState: this.onGameState.bind(this),
                debug: this.props.debug, endpoint: this.props.endpoint
            }
        )
        this.state = new Observed(new ChessConsoleAppState())
        this.state.addObserver(() => {
            const sessionDataValue = get(sessionData)
            sessionDataValue.playerState = this.state.playerState
            sessionData.set(sessionDataValue)
        }, ["playerState"])
        // this.state.playerState = PLAYER_STATE.appInit
        this.components = {
            chessConsole: undefined, // created on new match
            endOfGameActions: new EndOfGameActions(this)
        }
        this.webSocketConnection.open()
        this.state.playerState = PLAYER_STATE.lobbyGameRequestSent
        this.webSocketConnection.state.webSocket.addEventListener("open", () => {
            // this.webSocketConnection.sendReconnect(this.props.accessToken) // TODO
            this.sendLobbyGameRequest()
        })
    }

    onInitGame(data) {
        this.webSocketConnection.state.webSocket.send("CONTINUE")
        this.state.initGameData = data
        debugLog(this.props.debug, "onInitGame", data)
        this.state.playerState = PLAYER_STATE.initGameReceived
        if (data["whiteUID"] === this.props.playerUid) {
            this.playerColor = COLOR.white
        } else if (data["blackUID"] === this.props.playerUid) {
            this.playerColor = COLOR.black
        } else {
            console.error("this.props.playerUid", this.props.playerUid, "not found in InitGame data")
        }
        const sessionDataValue = get(sessionData)
        sessionDataValue.resultForPlayer = undefined
        sessionDataValue.stake = data.stake
        const [minutes, increment] = data.time_control_string.split("+")
        sessionDataValue.timeControlMinutes = minutes
        sessionDataValue.timeControlIncrementSeconds = increment
        sessionData.set(sessionDataValue)

    }

    onGameState(data) {
        const gameStateBefore = this.state.gameState
        this.state.gameState = data
        debugLog(this.props.debug, "onGameState", data)
        if (this.state.playerState === PLAYER_STATE.initGameReceived) {
            this.state.playerState = PLAYER_STATE.playing
            debugLog(this.props.debug, "ChessConsoleApp.context (d64eb5)", this.context)
            // this.props.playPage.lookingForOpponent = false
            this.createChessConsoleInstance()
        } else if (this.state.playerState === PLAYER_STATE.playing) {
            if(this.state.gameState._uciMoves.length === gameStateBefore._uciMoves.length + 1) {
                this.components.chessConsole.onMove(this.state.gameState._uciMoves.length % 2 === 0 ? COLOR.black : COLOR.white, this.state.gameState._uciMoves[this.state.gameState._uciMoves.length - 1])
            }
        } else {
            console.error("wrong state, please reload browser", this.state.playerState)
        }
    }

    createChessConsoleInstance() {
        const chessConsole = this.components.chessConsole
        const sessionDataValue = get(sessionData)
        const props = {
            playerColor: this.playerColor,
            playerData: {
                name: this.playerColor === COLOR.white ? this.state.gameState["_white_name"] : this.state.gameState["_black_name"],
                type: GambitLocalPlayer,
                props: {
                    color: this.playerColor,
                    rating: this.playerColor === COLOR.white ?
                        this.state.initGameData.whiteRating : this.state.initGameData.blackRating
                }
            },
            opponentData: {
                name: this.playerColor === COLOR.white ? this.state.gameState["_black_name"] : this.state.gameState["_white_name"],
                type: GambitRemotePlayer,
                props: {
                    color: this.playerColor === COLOR.white ? COLOR.black : COLOR.white,
                    rating: this.playerColor === COLOR.white ?
                        this.state.initGameData.blackRating : this.state.initGameData.whiteRating
                }
            },
            animationDuration: sessionDataValue.timeControlMinutes < 3 ? 0 : 200,
            playPage: this.props.playPage
        }
        if(!chessConsole) {
            this.components.chessConsole = new GambitChessConsole(this, this.context.querySelector(".chessboard"), props)
        } else {
            this.components.chessConsole.initGame(props)
        }
    }

    sendLobbyGameRequest() {
        this.state.playerState = PLAYER_STATE.lobbyGameRequestSent
        this.webSocketConnection.sendLobbyGame(this.props.accessToken, this.props.timeControl, this.props.stake, undefined, this.props.increment)
    }

    sendRematchRequest() {
        this.components.chessConsole.player.premoves = []
        this.components.chessConsole.player.updatePremoveMarkers()
        this.webSocketConnection.sendGameEvent(this.props.accessToken, gameEventAction.REQUEST_REMATCH)
    }

    declineRematch() {
        const sessionDataValue = get(sessionData)
        sessionDataValue.showAcceptRematchDialog = false
        sessionData.set(sessionDataValue)
    }

    searchNewOpponent() {
        //this.webSocketConnection.state.webSocket.close()

        createAppAndSendGameRequest(this.props.playerUid, this.props.accessToken, this.props.timeControl, this.props.stake, 1)
    }

    showHome() {
        this.state.playerState = PLAYER_STATE.idle
        const sessionDataValue = get(sessionData)
        sessionDataValue.selectedCard = undefined
        sessionDataValue.cardLocked = false
        sessionData.set(sessionDataValue)
    }

    turnBoard() {
        if(this.components.chessConsole) {
            const chessboard = this.components.chessConsole.components.board.chessboard
            chessboard.setOrientation(chessboard.getOrientation() === COLOR.white ? COLOR.black :COLOR.white)
        }
    }
}

export function createAppAndSendGameRequest(playerUid, accessToken, timeControlName, stake, inc, initialFen = undefined) {
    const sessionDataValue = get(sessionData)
    sessionDataValue.chessConsoleApp = new ChessConsoleApp(document.getElementById("chess-console-app"), {
        endpoint: sessionDataValue.wsEndpoint, // todo put this into a central configuration
        debug: sessionDataValue.debug, // todo put this into a central configuration and remove for production
        playerUid: sessionDataValue.user.uid,
        accessToken: accessToken,
        timeControl: timeControlName,
        stake: stake,
        increment: inc

    })
  if (initialFen) {
        sessionDataValue.chessConsoleApp.components.chessConsole.state.chess.load(initialFen);
        sessionDataValue.chessConsoleApp.components.board.setPositionOfPlyViewed(false); // Update the board to reflect the new position
    } else if (timeControlName !== 'puzzle') { // Only send lobby game request if timeControlName is not 'puzzle'
        sessionDataValue.chessConsoleApp.sendLobbyGameRequest();
    }
}
